import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MessageCircle, Reply, Trash2, Send } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "@/hooks/use-toast";
import type { CommentEntry } from "@/domain/rdo/schema";

type CommentsProps = {
  value: CommentEntry[];
  onChange: (comments: CommentEntry[]) => void;
  mode: "create" | "edit" | "view";
  currentUserId?: string;
  disabled?: boolean;
};

export function CommentsModule({ value, onChange, mode, currentUserId, disabled }: CommentsProps) {
  const [newCommentText, setNewCommentText] = useState("");
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");
  const [deletingComment, setDeletingComment] = useState<string | null>(null);

  const isReadOnly = mode === "view" || disabled;

  const handleAddComment = () => {
    if (!newCommentText.trim()) {
      toast({
        title: "Erro",
        description: "O comentário não pode estar vazio.",
        variant: "destructive",
      });
      return;
    }

    const newComment: CommentEntry = {
      id: crypto.randomUUID(),
      authorId: currentUserId || "current-user",
      authorName: "Usuário Atual",
      avatarUrl: undefined,
      createdAt: new Date().toISOString(),
      text: newCommentText.trim(),
      canDelete: true,
      canReply: true,
      replies: [],
    };

    onChange([...value, newComment]);
    setNewCommentText("");

    toast({
      title: "Sucesso",
      description: "Comentário adicionado com sucesso.",
    });
  };

  const handleReply = (parentId: string) => {
    if (!replyText.trim()) {
      toast({
        title: "Erro",
        description: "A resposta não pode estar vazia.",
        variant: "destructive",
      });
      return;
    }

    const reply: CommentEntry = {
      id: crypto.randomUUID(),
      authorId: currentUserId || "current-user",
      authorName: "Usuário Atual",
      avatarUrl: undefined,
      createdAt: new Date().toISOString(),
      text: replyText.trim(),
      canDelete: true,
      canReply: true,
      replies: [],
    };

    const updatedComments = value.map((comment) => {
      if (comment.id === parentId) {
        return {
          ...comment,
          replies: [...(comment.replies || []), reply],
        };
      }
      return comment;
    });

    onChange(updatedComments);
    setReplyingTo(null);
    setReplyText("");

    toast({
      title: "Sucesso",
      description: "Resposta adicionada com sucesso.",
    });
  };

  const handleDeleteComment = (commentId: string) => {
    const deleteFromComments = (comments: CommentEntry[]): CommentEntry[] => {
      return comments
        .filter((comment) => comment.id !== commentId)
        .map((comment) => ({
          ...comment,
          replies: comment.replies ? deleteFromComments(comment.replies) : [],
        }));
    };

    onChange(deleteFromComments(value));
    setDeletingComment(null);

    toast({
      title: "Sucesso",
      description: "Comentário excluído com sucesso.",
    });
  };

  const canDeleteComment = (comment: CommentEntry): boolean => {
    return !isReadOnly && (comment.canDelete || comment.authorId === currentUserId);
  };

  const canReplyToComment = (comment: CommentEntry): boolean => {
    return !isReadOnly && comment.canReply;
  };

  const formatCommentDate = (dateString: string, updatedAt?: string) => {
    try {
      const date = new Date(dateString);
      const formatted = formatDistanceToNow(date, { locale: ptBR, addSuffix: true });
      const edited = updatedAt && updatedAt !== dateString ? " (editado)" : "";
      return formatted + edited;
    } catch {
      return "Data inválida";
    }
  };

  const renderComment = (comment: CommentEntry, level = 0) => {
    const marginLeft = level * 24;

    return (
      <div key={comment.id} style={{ marginLeft: `${marginLeft}px` }} className="mb-4">
        <Card className="border border-border">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Avatar className="h-8 w-8">
                <AvatarImage src={comment.avatarUrl} />
                <AvatarFallback className="text-xs">
                  {comment.authorName.substring(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-medium text-foreground">{comment.authorName}</span>
                  <span className="text-xs text-muted-foreground">
                    {formatCommentDate(comment.createdAt, comment.updatedAt)}
                  </span>
                </div>
                
                <p className="text-foreground text-sm whitespace-pre-wrap mb-3">
                  {comment.text}
                </p>
                
                <div className="flex items-center gap-2">
                  {canReplyToComment(comment) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setReplyingTo(comment.id)}
                      className="h-auto p-1 text-muted-foreground hover:text-foreground"
                      aria-label={`Responder ao comentário de ${comment.authorName}`}
                    >
                      <Reply className="h-3 w-3 mr-1" />
                      Responder
                    </Button>
                  )}
                  
                  {canDeleteComment(comment) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeletingComment(comment.id)}
                      className="h-auto p-1 text-muted-foreground hover:text-destructive"
                      aria-label={`Excluir comentário de ${comment.authorName}`}
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Excluir
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {replyingTo === comment.id && (
          <div className="mt-3 ml-8">
            <Card className="border border-border">
              <CardContent className="p-3">
                <Textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Escreva sua resposta..."
                  className="mb-2 resize-none"
                  rows={3}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleReply(comment.id);
                    }
                    if (e.key === "Escape") {
                      setReplyingTo(null);
                      setReplyText("");
                    }
                  }}
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleReply(comment.id)}
                    disabled={!replyText.trim()}
                  >
                    <Send className="h-3 w-3 mr-1" />
                    Responder
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setReplyingTo(null);
                      setReplyText("");
                    }}
                  >
                    Cancelar
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {comment.replies && comment.replies.length > 0 && (
          <div className="mt-4">
            {comment.replies
              .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
              .map((reply) => renderComment(reply, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const sortedComments = [...value].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  return (
    <div className="space-y-4">
      {/* Add new comment */}
      {!isReadOnly && (
        <Card className="border border-border">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="text-xs">U</AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <Textarea
                  value={newCommentText}
                  onChange={(e) => setNewCommentText(e.target.value)}
                  placeholder="Adicione um comentário..."
                  className="mb-2 resize-none"
                  rows={3}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleAddComment();
                    }
                  }}
                />
                <div className="flex justify-between items-center">
                  <Badge variant="secondary" className="text-xs">
                    <MessageCircle className="h-3 w-3 mr-1" />
                    Enter para enviar, Shift+Enter para quebrar linha
                  </Badge>
                  <Button
                    size="sm"
                    onClick={handleAddComment}
                    disabled={!newCommentText.trim()}
                  >
                    <Send className="h-3 w-3 mr-1" />
                    Comentar
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Comments list */}
      {sortedComments.length === 0 ? (
        <Alert>
          <MessageCircle className="h-4 w-4" />
          <AlertDescription>
            Nenhum comentário ainda. {!isReadOnly && "Seja o primeiro a comentar!"}
          </AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-4">
          {sortedComments.map((comment) => renderComment(comment))}
        </div>
      )}

      {/* Delete confirmation */}
      <AlertDialog open={!!deletingComment} onOpenChange={() => setDeletingComment(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir comentário</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza de que deseja excluir este comentário? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingComment && handleDeleteComment(deletingComment)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}